import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageLaundry = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    hostelId: '',
    serviceName: '',
    category: '',
    price: '',
    duration: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchServices();
      fetchHostels();
    }
  }, [userInfo]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/superadmin/laundry/services/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setServices(data.services || []);
      } else {
        setError(data.message || 'Failed to load services');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to connect to server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();

    if (!formData.hostelId || !formData.serviceName || !formData.category || !formData.price) {
      alert('Please fill in all required fields');
      return;
    }

    if (submitting) return;

    try {
      setSubmitting(true);

      const response = await fetch(`${API_URL}/superadmin/laundry/service/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          duration: parseInt(formData.duration) || null
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Service added successfully!');
        setShowAddModal(false);
        resetForm();
        fetchServices();
      } else {
        alert(`❌ ${data.message || 'Failed to add service'}`);
      }
    } catch (err) {
      console.error('Error adding service:', err);
      alert('❌ Failed to add service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();

    if (!formData.serviceName || !formData.category || !formData.price) {
      alert('Please fill in all required fields');
      return;
    }

    if (submitting) return;

    try {
      setSubmitting(true);

      const response = await fetch(
        `${API_URL}/superadmin/laundry/service/${userInfo.userId}/${selectedService.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            serviceName: formData.serviceName,
            category: formData.category,
            price: parseFloat(formData.price),
            duration: parseInt(formData.duration) || null,
            description: formData.description,
            isActive: formData.isActive
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('✅ Service updated successfully!');
        setShowEditModal(false);
        setSelectedService(null);
        resetForm();
        fetchServices();
      } else {
        alert(`❌ ${data.message || 'Failed to update service'}`);
      }
    } catch (err) {
      console.error('Error updating service:', err);
      alert('❌ Failed to update service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteService = async () => {
    if (!selectedService || submitting) return;

    try {
      setSubmitting(true);

      const response = await fetch(
        `${API_URL}/superadmin/laundry/service/${userInfo.userId}/${selectedService.id}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('✅ Service deleted successfully!');
        setShowDeleteModal(false);
        setSelectedService(null);
        fetchServices();
      } else {
        alert(`❌ ${data.message || 'Failed to delete service'}`);
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('❌ Failed to delete service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (service) => {
    setSelectedService(service);
    setFormData({
      hostelId: service.hostel_id,
      serviceName: service.service_name,
      category: service.category,
      price: service.price,
      duration: service.duration_days || '',
      description: service.description || '',
      isActive: service.is_active
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (service) => {
    setSelectedService(service);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      hostelId: '',
      serviceName: '',
      category: '',
      price: '',
      duration: '',
      description: '',
      isActive: true
    });
  };

  const filteredServices = services.filter(service => {
    const matchesSearch =
      service.service_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.hostel_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.category?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesHostel = filterHostel === 'all' || service.hostel_id?.toString() === filterHostel;
    const matchesCategory = filterCategory === 'all' || service.category === filterCategory;

    return matchesSearch && matchesHostel && matchesCategory;
  });

  const getCategoryIcon = (category) => {
    const icons = {
      wash: '🧼',
      iron: '🔥',
      'dry-clean': '✨',
      'wash-iron': '🧺',
      fold: '📦',
      express: '⚡',
      other: '🏷️'
    };
    return icons[category] || '🧺';
  };

  const getCategoryBadgeClass = (category) => {
    const classes = {
      wash: 'category-wash',
      iron: 'category-iron',
      'dry-clean': 'category-dryclean',
      'wash-iron': 'category-washiron',
      fold: 'category-fold',
      express: 'category-express',
      other: 'category-other'
    };
    return classes[category] || 'category-other';
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-laundry"
      pageTitle="Manage Laundry Services 🧺"
    >
      <style>{`
        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          border: 2px solid rgba(82,171,152,0.15);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
        }

        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .add-btn {
          padding: 14px 28px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
        }

        .service-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          border: 2px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .service-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .service-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .service-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
        }

        .service-info {
          flex: 1;
        }

        .service-name {
          font-size: 18px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .service-hostel {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .service-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-label {
          font-size: 11px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
        }

        .price-tag {
          font-size: 24px;
          font-weight: 900;
          color: #52ab98;
        }

        .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .category-wash { background: rgba(59,130,246,0.15); color: #2563eb; }
        .category-iron { background: rgba(251,146,60,0.15); color: #ea580c; }
        .category-dryclean { background: rgba(168,85,247,0.15); color: #9333ea; }
        .category-washiron { background: rgba(34,197,94,0.15); color: #16a34a; }
        .category-fold { background: rgba(236,72,153,0.15); color: #db2777; }
        .category-express { background: rgba(234,179,8,0.15); color: #ca8a04; }
        .category-other { background: rgba(107,114,128,0.15); color: #4b5563; }

        .service-description {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          line-height: 1.5;
          margin-bottom: 16px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .status-indicator {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .service-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn-edit,
        .action-btn-delete {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn-edit {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-edit:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-delete {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: rgba(239,68,68,0.25);
          transform: translateY(-2px);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-label.required::after {
          content: ' *';
          color: #dc2626;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }

        .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .form-input:disabled,
        .form-select:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .checkbox-input {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel,
        .btn-submit {
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }

        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .btn-delete {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(220,38,38,0.3);
        }

        .btn-delete:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(220,38,38,0.4);
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        .delete-warning {
          background: rgba(239,68,68,0.1);
          border: 2px solid rgba(239,68,68,0.3);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .delete-warning-text {
          font-size: 15px;
          color: #dc2626;
          font-weight: 600;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .services-grid {
            grid-template-columns: 1fr;
          }

          .controls-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            width: 100%;
          }

          .stats-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total Services</div>
          <div className="stat-value">{services.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Services</div>
          <div className="stat-value">
            {services.filter(s => s.is_active).length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Hostels Covered</div>
          <div className="stat-value">
            {new Set(services.map(s => s.hostel_id)).size}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg. Price</div>
          <div className="stat-value">
            ₹{services.length > 0
              ? Math.round(services.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0) / services.length)
              : 0}
          </div>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by service name, hostel, or category..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_id}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">All Categories</option>
          <option value="wash">🧼 Wash</option>
          <option value="iron">🔥 Iron</option>
          <option value="dry-clean">✨ Dry Clean</option>
          <option value="wash-iron">🧺 Wash & Iron</option>
          <option value="fold">📦 Fold</option>
          <option value="express">⚡ Express</option>
          <option value="other">🏷️ Other</option>
        </select>

        <button className="add-btn" onClick={() => {
          resetForm();
          setShowAddModal(true);
        }}>
          ➕ Add Service
        </button>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading services...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🧺</div>
          <div className="empty-state-text">
            {searchQuery || filterHostel !== 'all' || filterCategory !== 'all'
              ? 'No services match your filters'
              : 'No laundry services found. Add your first service!'}
          </div>
        </div>
      ) : (
        <div className="services-grid">
          {filteredServices.map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-header">
                <div className="service-icon">
                  {getCategoryIcon(service.category)}
                </div>
                <div className="service-info">
                  <div className="service-name">{service.service_name}</div>
                  <div className="service-hostel">{service.hostel_name}</div>
                </div>
              </div>

              <div className="status-indicator" style={{
                background: service.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: service.is_active ? '#16a34a' : '#dc2626'
              }}>
                <span>{service.is_active ? '● Active' : '● Inactive'}</span>
              </div>

              <div className="service-details">
                <div className="detail-item">
                  <span className="detail-label">Category</span>
                  <span className={`category-badge ${getCategoryBadgeClass(service.category)}`}>
                    {getCategoryIcon(service.category)} {service.category}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Price</span>
                  <span className="price-tag">₹{service.price}</span>
                </div>
                {service.duration_days && (
                  <div className="detail-item">
                    <span className="detail-label">Duration</span>
                    <span className="detail-value">{service.duration_days} day{service.duration_days > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>

              {service.description && (
                <div className="service-description">{service.description}</div>
              )}

              <div className="service-actions">
                <button
                  className="action-btn-edit"
                  onClick={() => handleOpenEdit(service)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="action-btn-delete"
                  onClick={() => handleOpenDelete(service)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Service</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleAddService}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label required">Hostel</label>
                  <select
                    className="form-select"
                    value={formData.hostelId}
                    onChange={(e) => setFormData({ ...formData, hostelId: e.target.value })}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select Hostel</option>
                    {hostels.map(hostel => (
                      <option key={hostel.hostel_id} value={hostel.hostel_id}>
                        {hostel.hostel_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">Service Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.serviceName}
                    onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                    placeholder="e.g., Regular Wash"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Category</label>
                  <select
                    className="form-select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select Category</option>
                    <option value="wash">🧼 Wash</option>
                    <option value="iron">🔥 Iron</option>
                    <option value="dry-clean">✨ Dry Clean</option>
                    <option value="wash-iron">🧺 Wash & Iron</option>
                    <option value="fold">📦 Fold</option>
                    <option value="express">⚡ Express</option>
                    <option value="other">🏷️ Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">Price (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Duration (Days)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="e.g., 2"
                    min="1"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the service details..."
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      disabled={submitting}
                    />
                    <label htmlFor="isActive" className="form-label" style={{ marginBottom: 0 }}>
                      Service is Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? '⏳ Adding...' : '✅ Add Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedService && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Service</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateService}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Hostel</label>
                  <input
                    type="text"
                    className="form-input"
                    value={selectedService.hostel_name}
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Service Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.serviceName}
                    onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Category</label>
                  <select
                    className="form-select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select Category</option>
                    <option value="wash">🧼 Wash</option>
                    <option value="iron">🔥 Iron</option>
                    <option value="dry-clean">✨ Dry Clean</option>
                    <option value="wash-iron">🧺 Wash & Iron</option>
                    <option value="fold">📦 Fold</option>
                    <option value="express">⚡ Express</option>
                    <option value="other">🏷️ Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">Price (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    step="0.01"
                    min="0"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Duration (Days)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    min="1"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      id="editIsActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      disabled={submitting}
                    />
                    <label htmlFor="editIsActive" className="form-label" style={{ marginBottom: 0 }}>
                      Service is Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEditModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? '⏳ Updating...' : '✅ Update Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && selectedService && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ color: '#dc2626' }}>Delete Service</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-warning">
                <p className="delete-warning-text">
                  Are you sure you want to delete the service <strong>{selectedService.service_name}</strong>?
                  This action cannot be undone.
                </p>
              </div>

              <div className="detail-item">
                <span className="detail-label">Hostel</span>
                <span className="detail-value">{selectedService.hostel_name}</span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit btn-delete"
                onClick={handleDeleteService}
                disabled={submitting}
              >
                {submitting ? '⏳ Deleting...' : '🗑️ Delete Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageLaundry;